/**
 * VoiceReplySpeaker — speaks new assistant replies aloud when the user has
 * enabled "Voice replies" in Settings → General (off by default).
 *
 * ARCHITECTURE_LOCKED.md chunk A2, per Brad 2026-07-07: voice OUT is a
 * platform setting, not a front-and-center control. This component renders
 * nothing; it observes the message list and drives useWebVoice.
 *
 * Speaking rules:
 *  - Only the newest message, only role "assistant", only real content
 *    (system/provider notices are skipped).
 *  - Never speaks history: anything present at mount (conversation load /
 *    switch) is marked already-seen.
 *  - Debounced 1.2s so streaming/progressive updates settle before TTS.
 *  - A new user message cancels any in-flight speech.
 */

import { useEffect, useRef } from "react";
import { useWebVoice } from "../hooks/useWebVoice";

const SETTING_KEY = "voiceReplies";
// Fired whenever the setting flips, from ANY control (Settings toggle or the
// in-chat speaker button) so every listener stays in sync — and so this
// component can cut off in-flight speech the moment voice is turned off.
export const VOICE_REPLIES_EVENT = "optimus-voice-replies-changed";
// Fired by this component as speech starts/ends, so the chat button can show
// a stop affordance while a reply is actually being read.
export const VOICE_SPEAKING_EVENT = "optimus-voice-speaking-changed";
// Barge-in command: stop the CURRENT speech without touching the setting
// (Orca's ESC — "shut it up mid-sentence", voice replies stay on).
const VOICE_STOP_EVENT = "optimus-voice-stop";

export function voiceRepliesEnabled() {
  return localStorage.getItem(SETTING_KEY) === "on";
}

/** Single write path for the setting — keeps chat button + Settings in sync. */
export function setVoiceRepliesEnabled(enabled) {
  localStorage.setItem(SETTING_KEY, enabled ? "on" : "off");
  window.dispatchEvent(new CustomEvent(VOICE_REPLIES_EVENT, { detail: { enabled } }));
}

/** Stop whatever is being spoken right now. Setting unchanged. */
export function requestStopSpeaking() {
  window.dispatchEvent(new CustomEvent(VOICE_STOP_EVENT));
}

const messageText = (m) => {
  if (!m) return "";
  if (typeof m.content === "string") return m.content;
  if (Array.isArray(m.contentBlocks)) {
    return m.contentBlocks
      .filter((b) => b.type === "text")
      .map((b) => b.content)
      .join("\n");
  }
  return "";
};

const messageKey = (m, idx) => `${idx}:${m?.timestamp || 0}`;

const VoiceReplySpeaker = ({ messages, conversationId }) => {
  const { speakText, stopSpeaking, isSpeaking } = useWebVoice();
  const seenRef = useRef(new Set());
  const mountedRef = useRef(false);
  const timerRef = useRef(null);
  const conversationRef = useRef(conversationId);

  // Turning voice replies OFF (from Settings or the chat speaker button)
  // silences any reply that's mid-sentence — off means off, immediately.
  // A bare stop request (the chat button's ⏹ while speaking) kills the
  // current reply only; the setting stays on for the next one.
  useEffect(() => {
    const onChange = (e) => {
      if (e?.detail?.enabled === false) {
        if (timerRef.current) clearTimeout(timerRef.current);
        stopSpeaking();
      }
    };
    const onStop = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      stopSpeaking();
    };
    window.addEventListener(VOICE_REPLIES_EVENT, onChange);
    window.addEventListener("optimus-voice-stop", onStop);
    return () => {
      window.removeEventListener(VOICE_REPLIES_EVENT, onChange);
      window.removeEventListener("optimus-voice-stop", onStop);
    };
  }, [stopSpeaking]);

  // Broadcast speaking state so the chat button can offer stop-mid-speech.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent(VOICE_SPEAKING_EVENT, { detail: { speaking: isSpeaking } }),
    );
  }, [isSpeaking]);

  useEffect(() => {
    const list = messages || [];

    // First render WITH CONTENT: mark everything as seen — never read out
    // loaded history. On a page refresh the messages load async, so the very
    // first render is often empty; we must wait for the real list to arrive
    // before marking-seen, or the last loaded reply gets spoken on refresh.
    if (!mountedRef.current) {
      if (list.length === 0) return; // not loaded yet — wait for content
      list.forEach((m, i) => seenRef.current.add(messageKey(m, i)));
      mountedRef.current = true;
      return;
    }

    // Conversation switch: the whole list is replaced with loaded history.
    // Mark it all seen and stay silent — revisiting a chat must never
    // re-speak old replies.
    if (conversationId !== conversationRef.current) {
      conversationRef.current = conversationId;
      seenRef.current = new Set();
      list.forEach((m, i) => seenRef.current.add(messageKey(m, i)));
      stopSpeaking();
      return;
    }

    // Bulk arrival (>1 unseen at once) is a history load, not a live reply.
    const unseen = list.filter((m, i) => !seenRef.current.has(messageKey(m, i)));
    if (unseen.length > 1) {
      list.forEach((m, i) => seenRef.current.add(messageKey(m, i)));
      return;
    }

    const idx = list.length - 1;
    const tail = list[idx];
    if (!tail) return;
    const key = messageKey(tail, idx);
    if (seenRef.current.has(key)) return;

    if (tail.role === "user") {
      seenRef.current.add(key);
      stopSpeaking(); // user interjected — shut up and listen
      return;
    }

    if (tail.role !== "assistant" || tail.provider === "system") {
      seenRef.current.add(key);
      return;
    }

    if (!voiceRepliesEnabled()) {
      seenRef.current.add(key);
      return;
    }

    // Debounce: wait for the message to stop changing (streaming settles),
    // then speak the final text once.
    if (timerRef.current) clearTimeout(timerRef.current);
    const lengthAtSchedule = messageText(tail).length;
    timerRef.current = setTimeout(() => {
      const now = messages?.[idx];
      const text = messageText(now);
      if (!text || text.length !== lengthAtSchedule) return; // still streaming — next effect re-arms
      seenRef.current.add(key);
      speakText(text).catch(() => {
        /* TTS failure is non-fatal — text is on screen */
      });
    }, 1200);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [messages, conversationId]);

  return null;
};

export default VoiceReplySpeaker;
